import type { DashboardImgr } from "@caint/data";
import type { ReactNode } from "react";
import {
  BarrasAgrupadas,
  BarrasClassificadas,
  BarrasDivergentes,
  DefinicoesNiveis,
  Dispersao,
  LegendaFaixas,
  Radar,
  Velocimetro,
} from "./graficos.tsx";
import "../styles/imgr.css";

const COR_GESTOR = "var(--primary)";
const COR_LIDERADO = "var(--mat-3)";

function pct(n: number | null): string {
  return n == null ? "—" : `${n.toFixed(1).replace(".", ",")}%`;
}
function pp(n: number): string {
  return `${n > 0 ? "+" : ""}${n.toFixed(0)} pp`;
}

function Cartao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="m-card pad">
      <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text-muted)", marginBottom: 10 }}>
        {titulo}
      </div>
      {children}
    </div>
  );
}

/** Comparativo de percepção Gestor (líder) × Liderado, com gráficos diversos das diferenças. */
export function ComparativoPercepcao({ d, unidade }: { d: DashboardImgr; unidade?: string }) {
  const semGrupo = d.nGestores === 0 || d.nLiderados === 0;
  const hoje = new Date().toLocaleDateString("pt-BR");

  // Gap por componente (Gestor − Liderado), só onde os dois grupos responderam.
  const comps = d.porComponentePerfil
    .filter((c) => c.pctGestor != null && c.pctLiderado != null)
    .map((c) => ({
      id: c.id,
      nome: c.nome,
      g: c.pctGestor as number,
      l: c.pctLiderado as number,
      gap: (c.pctGestor as number) - (c.pctLiderado as number),
    }));
  const compsPorDivergencia = [...comps].sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap));

  // Gap por questão (top divergências).
  const questoes = d.questoes
    .filter((q) => q.pctGestor != null && q.pctLiderado != null)
    .map((q) => ({ q, gap: (q.pctGestor as number) - (q.pctLiderado as number) }))
    .sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap))
    .slice(0, 8);

  const gapGeral = d.pctGestor != null && d.pctLiderado != null ? d.pctGestor - d.pctLiderado : null;
  const divMedia = comps.length > 0 ? comps.reduce((s, c) => s + Math.abs(c.gap), 0) / comps.length : null;

  // Cobertura real (só com matriz da unidade): esperado × respondido.
  const esperadoTotal = d.esperadoTotal;
  const participacao =
    esperadoTotal != null && esperadoTotal > 0 ? (d.nRespondentes / esperadoTotal) * 100 : null;
  const faltando = [...d.coberturaCargos]
    .filter((c) => c.respondido < c.esperado)
    .sort((a, b) => b.esperado - b.respondido - (a.esperado - a.respondido))
    .slice(0, 10);

  return (
    <div className="imgr-print">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 10,
        }}
      >
        <div>
          <div style={{ fontWeight: 800, fontSize: 16 }}>
            Comparativo Líder × Liderado{unidade ? ` — ${unidade}` : ""}
          </div>
          <div className="spec">
            IMGR · {d.nRespondentes} respostas · gerado em {hoje}
          </div>
        </div>
        <button type="button" className="m-btn no-print" onClick={() => window.print()}>
          Imprimir / PDF
        </button>
      </div>

      {/* Hero: dois velocímetros + gap geral */}
      <div className="m-card pad imgr-hero">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ textAlign: "center", fontWeight: 700, color: COR_GESTOR }}>Líder (Gestor)</div>
            <Velocimetro
              valor={d.pctGestor}
              cor={COR_GESTOR}
              legenda={`${d.nGestores} resp.`}
              tamanho={150}
            />
          </div>
          <div style={{ textAlign: "center", padding: "0 6px" }}>
            <div className="spec">diferença</div>
            <div
              style={{
                fontSize: 26,
                fontWeight: 800,
                color: (gapGeral ?? 0) >= 0 ? COR_GESTOR : COR_LIDERADO,
              }}
            >
              {gapGeral == null ? "—" : pp(gapGeral)}
            </div>
            {gapGeral != null && (
              <div className="spec" style={{ maxWidth: 150 }}>
                {gapGeral >= 0 ? "líderes percebem MAIS maturidade" : "liderados percebem MAIS maturidade"}
              </div>
            )}
          </div>
          <div>
            <div style={{ textAlign: "center", fontWeight: 700, color: COR_LIDERADO }}>Liderado</div>
            <Velocimetro
              valor={d.pctLiderado}
              cor={COR_LIDERADO}
              legenda={`${d.nLiderados} resp.`}
              tamanho={150}
            />
          </div>
        </div>
        {divMedia != null && (
          <div className="spec" style={{ textAlign: "center", marginTop: 6 }}>
            Divergência média entre os grupos: <b>{divMedia.toFixed(0)} pp</b> por componente.
          </div>
        )}
      </div>

      {semGrupo && (
        <div className="m-card pad" style={{ marginBottom: 12, color: "var(--warning)" }}>
          ⚠️ Ainda falta resposta de {d.nGestores === 0 ? "gestores (líderes)" : "liderados"} — o comparativo
          fica completo quando os dois grupos respondem.
        </div>
      )}

      {/* Classificação por nível de maturidade (Manual IMGR · Tabela 3) — por componente */}
      <div className="m-card pad" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text-muted)", marginBottom: 10 }}>
          Classificação por nível de maturidade (Manual IMGR · Tabela 3) — Líder × Liderado × Total
        </div>
        <LegendaFaixas />
        <BarrasClassificadas
          grupos={[
            ...d.porComponentePerfil.map((c) => ({
              rotulo: `${c.id} · ${c.nome}`,
              valores: [
                { nome: "Líder", valor: c.pctGestor },
                { nome: "Liderado", valor: c.pctLiderado },
                { nome: "Total", valor: c.pctGeral },
              ],
            })),
            {
              rotulo: "IMGR geral",
              valores: [
                { nome: "Líder", valor: d.pctGestor },
                { nome: "Liderado", valor: d.pctLiderado },
                { nome: "Total", valor: d.imgr },
              ],
            },
          ]}
        />
        <DefinicoesNiveis />
      </div>

      <div className="imgr-cols">
        <Cartao titulo="Radar · maturidade por componente">
          <Radar
            eixos={d.porComponentePerfil.map((c) => c.id)}
            series={[
              { nome: "Líder", cor: COR_GESTOR, valores: d.porComponentePerfil.map((c) => c.pctGestor) },
              {
                nome: "Liderado",
                cor: COR_LIDERADO,
                valores: d.porComponentePerfil.map((c) => c.pctLiderado),
              },
            ]}
          />
        </Cartao>

        <Cartao titulo="Onde mais divergem · por componente (Líder − Liderado)">
          <div style={{ display: "flex", gap: 14, marginBottom: 8, fontSize: 12 }}>
            <span style={{ color: COR_GESTOR, fontWeight: 700 }}>■ líder percebe mais →</span>
            <span style={{ color: COR_LIDERADO, fontWeight: 700 }}>← liderado percebe mais</span>
          </div>
          <BarrasDivergentes
            itens={compsPorDivergencia.map((c) => ({ rotulo: c.nome, valor: c.gap }))}
            corPos={COR_GESTOR}
            corNeg={COR_LIDERADO}
            sufixo=" pp"
          />
        </Cartao>

        <Cartao titulo="Dispersão · concordância por componente">
          <Dispersao
            pontos={comps.map((c) => ({ x: c.l, y: c.g, rotulo: c.id }))}
            rotuloX="Liderado"
            rotuloY="Líder"
          />
          <div className="spec" style={{ marginTop: 4 }}>
            Pontos na diagonal = percepções iguais. Acima = líder mais otimista; abaixo = liderado mais
            otimista.
          </div>
        </Cartao>

        <Cartao titulo="Perguntas que mais dividem opiniões">
          <div style={{ display: "flex", gap: 14, marginBottom: 8, fontSize: 12 }}>
            <span style={{ color: COR_GESTOR, fontWeight: 700 }}>■ líder mais →</span>
            <span style={{ color: COR_LIDERADO, fontWeight: 700 }}>← liderado mais</span>
          </div>
          <BarrasDivergentes
            itens={questoes.map(({ q, gap }) => ({
              rotulo: `${q.componente}.${q.num} ${q.texto}`,
              valor: gap,
            }))}
            corPos={COR_GESTOR}
            corNeg={COR_LIDERADO}
            sufixo=" pp"
          />
        </Cartao>

        <Cartao titulo="Cobertura por área · quem respondeu (líder × liderado)">
          {participacao != null && (
            <div style={{ marginBottom: 10 }}>
              <div
                style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 3 }}
              >
                <span>
                  Participação: <b>{d.nRespondentes}</b> de <b>{esperadoTotal}</b> esperados
                </span>
                <b style={{ color: participacao >= 60 ? "var(--mat-3)" : "var(--warning)" }}>
                  {participacao.toFixed(0)}%
                </b>
              </div>
              <div
                style={{ height: 9, borderRadius: 99, background: "var(--surface-2)", overflow: "hidden" }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${Math.min(100, participacao)}%`,
                    background: participacao >= 60 ? "var(--mat-3)" : "var(--warning)",
                    borderRadius: 99,
                  }}
                />
              </div>
            </div>
          )}
          <BarrasAgrupadas
            itens={d.coberturaAreas.map((c) => ({ rotulo: c.area, a: c.gestores, b: c.liderados }))}
            corA={COR_GESTOR}
            corB={COR_LIDERADO}
            nomeA="Líder"
            nomeB="Liderado"
          />
          {esperadoTotal != null && (
            <div style={{ marginTop: 8, display: "grid", gap: 3 }}>
              {d.coberturaAreas.map((c) => (
                <div
                  key={c.area}
                  className="spec"
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span>{c.area}</span>
                  <span>
                    Líder{" "}
                    <b>
                      {c.gestores}/{c.esperadoGestor ?? 0}
                    </b>{" "}
                    · Liderado{" "}
                    <b>
                      {c.liderados}/{c.esperadoLiderado ?? 0}
                    </b>
                  </span>
                </div>
              ))}
            </div>
          )}
          {faltando.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div className="spec" style={{ fontWeight: 700, marginBottom: 3 }}>
                Cargos com lacuna (respondido / esperado):
              </div>
              <div style={{ display: "grid", gap: 2 }}>
                {faltando.map((c) => (
                  <div
                    key={c.cargo}
                    className="spec"
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "70%",
                      }}
                    >
                      {c.cargo}
                    </span>
                    <b style={{ color: c.respondido === 0 ? "var(--danger)" : "var(--warning)" }}>
                      {c.respondido}/{c.esperado}
                    </b>
                  </div>
                ))}
              </div>
            </div>
          )}
          {d.areasSemResposta.length > 0 ? (
            <div className="spec" style={{ marginTop: 8, color: "var(--danger)" }}>
              Áreas sem nenhuma resposta: <b>{d.areasSemResposta.join(", ")}</b>.
            </div>
          ) : (
            <div className="spec" style={{ marginTop: 8 }}>
              Todas as áreas têm ao menos uma resposta. ✓
            </div>
          )}
          {d.coberturaAreas.some(
            (c) => (c.esperaGestor && c.gestores === 0) || (c.esperaLiderado && c.liderados === 0),
          ) && (
            <div className="spec" style={{ marginTop: 4 }}>
              Falta um dos perfis em:{" "}
              <b>
                {d.coberturaAreas
                  .filter(
                    (c) => (c.esperaGestor && c.gestores === 0) || (c.esperaLiderado && c.liderados === 0),
                  )
                  .map(
                    (c) => `${c.area} (falta ${c.gestores === 0 && c.esperaGestor ? "líder" : "liderado"})`,
                  )
                  .join("; ")}
              </b>
              .
            </div>
          )}
        </Cartao>

        <Cartao titulo="Detalhe por componente">
          <div style={{ display: "grid", gap: 6 }}>
            <div
              className="spec"
              style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}
            >
              <span>Componente</span>
              <span>
                <span style={{ color: COR_GESTOR }}>Líder</span> ·{" "}
                <span style={{ color: COR_LIDERADO }}>Lid.</span> · gap
              </span>
            </div>
            {compsPorDivergencia.map((c) => (
              <div key={c.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}>
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "55%",
                  }}
                >
                  <b>{c.id}</b> {c.nome}
                </span>
                <span>
                  <b style={{ color: COR_GESTOR }}>{pct(c.g)}</b> ·{" "}
                  <b style={{ color: COR_LIDERADO }}>{pct(c.l)}</b> ·{" "}
                  <b style={{ color: c.gap >= 0 ? COR_GESTOR : COR_LIDERADO }}>{pp(c.gap)}</b>
                </span>
              </div>
            ))}
          </div>
        </Cartao>
      </div>
    </div>
  );
}
